import { ExcelExportParams, IRowNode } from "@ag-grid-community/core";
import { AgGridReact } from "@ag-grid-community/react";
import { Button, IconButton, Tooltip, useDisclosure } from "@chakra-ui/react";
import { BooleanTableCellValue } from "gen/model/booleanTableCellValue";
import { DateTableCellValue } from "gen/model/dateTableCellValue";
import { DatetimeTableCellValue } from "gen/model/datetimeTableCellValue";
import { DecimalTableCellValue } from "gen/model/decimalTableCellValue";
import { FormulaTableCellValue } from "gen/model/formulaTableCellValue";
import { GenericTableRow } from "gen/model/genericTableRow";
import { IntegerTableCellValue } from "gen/model/integerTableCellValue";
import { MoneyTableCellValue } from "gen/model/moneyTableCellValue";
import { PercentTableCellValue } from "gen/model/percentTableCellValue";
import { TextTableCellValue } from "gen/model/textTableCellValue";
import { cloneDeep, isEqual, mapValues, toPairs } from "lodash-es";
import React, { useCallback, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { SimpleTextInput } from "shared/components/input/text/SimpleTextInput";
import { useAgGridEventState } from "shared/components/table/ag/AgGridEventState";
import { getFlattenedColDefIds } from "shared/components/table/ag/AgGridUtils";
import {
  TableBoxActions,
  TableBoxActionsProps,
} from "shared/components/table/box/TableBoxActions";
import { useTableBoxContext } from "shared/components/table/box/TableBoxContext";
import { dataTableContextFromAgGrid } from "shared/components/table/data/DataTableUtils";
import { MaybernTableColumnsPopover } from "shared/components/table/maybern/MaybernTableColumnsPopover";
import { maybernTableExcelExportParams } from "shared/components/table/maybern/MaybernTableExport";
import { useExportGridStateUrl } from "shared/components/table/maybern/MaybernTableGridState";
import { useMaybernTableUIContext } from "shared/components/table/maybern/MaybernTableUIContext";
import { tryGetAgGridApi } from "shared/components/table/maybern/MaybernTableUtils";
import { ColumnsHiddenIcon } from "shared/icons/ColumnsHiddenIcon";
import { ColumnsIcon } from "shared/icons/ColumnsIcon";
import { FileDownloadIcon } from "shared/icons/FileDownloadIcon";
import { PivotIcon } from "shared/icons/PivotIcon";
import { RowCollapseIcon } from "shared/icons/RowCollapseIcon";
import { RowExpandIcon } from "shared/icons/RowExpandIcon";
import { SearchIcon } from "shared/icons/SearchIcon";
import { ShareIcon } from "shared/icons/ShareIcon";
import { sleep } from "shared/utils/utils";

type ShowClearConfig =
  | {
      display: false;
    }
  | {
      display: true;
      onClear: () => void;
    };

export interface MaybernTableBoxActionsProps extends TableBoxActionsProps {
  showSearch?: boolean;
  showExpand?: boolean;
  showPivot?: boolean;
  showColControl?: boolean;
  showClearConfig?: ShowClearConfig;

  createAction?: React.ReactNode;
  suppressReset?: React.ReactNode;
}

export const MaybernTableBoxActions: React.FC<
  React.PropsWithChildren<MaybernTableBoxActionsProps>
> = ({
  showSearch = true,
  showExpand = true,
  showPivot = false,
  showColControl = true,
  children,
  createAction,
  showClearConfig = { display: false },
  suppressReset = false,
  ...props
}) => {
  const { isReady, agGrid } = useMaybernTableUIContext();

  const isEmpty =
    useAgGridEventState(
      agGrid,
      "modelUpdated",
      () => dataTableContextFromAgGrid(agGrid)?.isEmpty,
    ) ?? false;

  if (!isReady) {
    return null;
  }

  return (
    <TableBoxActions {...props}>
      {!isEmpty && !suppressReset && <TableResetButton />}
      {createAction && (
        <TableBoxCreateAction>{createAction}</TableBoxCreateAction>
      )}
      {showClearConfig.display && (
        <TableClearFormButton onClear={showClearConfig.onClear} />
      )}
      {children}
      {showExpand && <TableExpandCollapseButton />}
      {showSearch && !isEmpty && <TableBoxSearchButton />}
      {showPivot && !isEmpty && <MaybernTablePivotButton />}
      {showColControl && <TableColumnsButton />}
    </TableBoxActions>
  );
};

const TableBoxSearchButton: React.FC = () => {
  const { agGrid } = useMaybernTableUIContext();
  const [text, setText] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);
  const { t } = useTranslation(["common"]);
  useEffect(() => {
    const api = tryGetAgGridApi(agGrid);
    if (!api) {
      return;
    }
    if (isExpanded) {
      api.setGridOption("quickFilterText", text);
    } else {
      setText("");
      api.setGridOption("quickFilterText", "");
    }
  }, [agGrid, isExpanded, text]);
  if (isExpanded) {
    return (
      <SimpleTextInput
        size="xs"
        borderRadius="6px"
        autoFocus={true}
        placeholder={t("common:search")}
        value={text}
        onChangeValue={setText}
        onClear={() => setIsExpanded(false)}
        controlProps={{
          maxWidth: "250px",
        }}
      />
    );
  }
  return (
    <Tooltip label={t("common:search")}>
      <IconButton
        variant="ghost"
        size="tablebox-icon"
        aria-label={t("common:search")}
        colorScheme="secondary"
        onClick={() => setIsExpanded(true)}
        icon={<SearchIcon />}
      />
    </Tooltip>
  );
};

const MaybernTablePivotButton: React.FC = () => {
  const { t } = useTranslation(["common"]);
  const { agGrid } = useMaybernTableUIContext();
  const { isFullScreen, setFullScreen } = useTableBoxContext();

  const isPivoting = useAgGridEventState(
    agGrid,
    "columnPivotModeChanged",
    (api) => api.isPivotMode(),
  );

  const onClick = async () => {
    const api = tryGetAgGridApi(agGrid);
    if (!api) {
      return;
    }
    if (!api.isSideBarVisible()) {
      if (!isFullScreen) {
        setFullScreen(true);
        await sleep(100);
      }
      api.setGridOption("pivotMode", true);
      api.openToolPanel("columns");
      api.setSideBarVisible(true);
    } else {
      api.setSideBarVisible(false);
    }
  };

  return (
    <Tooltip label={t("common:table.pivot")}>
      <IconButton
        variant="ghost"
        size="tablebox-icon"
        icon={<PivotIcon color={isPivoting ? "primary.600" : undefined} />}
        aria-label={t("common:table.pivot")}
        onClick={onClick}
      />
    </Tooltip>
  );
};

const TableColumnsButton: React.FC = () => {
  const { t } = useTranslation(["common"]);
  const { agGrid } = useMaybernTableUIContext();

  const { isOpen, onClose, onOpen } = useDisclosure();

  const hasColumns = useAgGridEventState(
    agGrid,
    "columnEverythingChanged",
    (api) => Boolean(api.getColumns()?.length),
  );

  const hasHiddenColumns = useAgGridEventState(
    agGrid,
    "columnEverythingChanged",
    (api) =>
      api
        .getColumns()
        ?.some((c) => c.isVisible() !== !c.getColDef().initialHide),
  );

  if (!hasColumns) {
    return null;
  }

  return (
    <MaybernTableColumnsPopover
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
    >
      <Tooltip label={t("common:table.columnView")}>
        <IconButton
          variant="ghost"
          size="tablebox-icon"
          aria-label={t("common:table.columnView")}
          icon={
            hasHiddenColumns ? (
              <ColumnsHiddenIcon color="primary.600" />
            ) : (
              <ColumnsIcon />
            )
          }
        />
      </Tooltip>
    </MaybernTableColumnsPopover>
  );
};

const TableExpandCollapseButton: React.FC = () => {
  const { t } = useTranslation(["common"]);

  const { agGrid } = useMaybernTableUIContext();

  const hasExpandableRows = useAgGridEventState(
    agGrid,
    "modelUpdated",
    (api) => {
      let found = false;
      api.forEachNode((node) => {
        if (node.isExpandable()) {
          found = true;
        }
      });
      return found;
    },
  );

  const hasExpandedRows = useAgGridEventState(agGrid, "modelUpdated", (api) =>
    api.getRenderedNodes().some((n) => !!(n.allChildrenCount && n.expanded)),
  );

  const toggleExpanded = useCallback(() => {
    const api = tryGetAgGridApi(agGrid);
    if (!api) {
      return;
    }
    if (hasExpandedRows) {
      api.collapseAll();
    } else {
      api.expandAll();
    }
  }, [agGrid, hasExpandedRows]);

  if (!hasExpandableRows) {
    return null;
  }

  const label = hasExpandedRows
    ? t("common:table.collapseAllRows")
    : t("common:table.expandAllRows");

  return (
    <Tooltip label={label}>
      <IconButton
        variant="ghost"
        size="tablebox-icon"
        colorScheme="secondary"
        aria-label={label}
        icon={hasExpandedRows ? <RowCollapseIcon /> : <RowExpandIcon />}
        onClick={toggleExpanded}
      />
    </Tooltip>
  );
};

const TableBoxCreateAction: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const { isReady, topLevelRowCount } = useMaybernTableUIContext();
  if (!isReady || topLevelRowCount === 0) {
    // create button is displayed by the empty state when the table is empty
    return null;
  }
  return children;
};

const TableResetButton: React.FC = () => {
  const { t } = useTranslation(["common"]);

  const { agGrid } = useMaybernTableUIContext();

  const didPositionChange = useAgGridEventState(
    agGrid,
    "columnMoved",
    (api) => {
      const origColIds = getFlattenedColDefIds(agGrid?.props.columnDefs);
      const curColIds = getFlattenedColDefIds(api.getColumnDefs());
      return !isEqual(origColIds, curColIds);
    },
  );

  const didVisibilityOrFilterChange = useAgGridEventState(
    agGrid,
    "columnEverythingChanged",
    (api) =>
      api
        .getColumns()
        ?.some(
          (c) =>
            c.isVisible() !== !c.getColDef().initialHide || c.isFilterActive(),
        ),
  );

  const didSortChange = useAgGridEventState(agGrid, "sortChanged", (api) =>
    api
      .getColumns()
      ?.some(
        (c) =>
          (c.getSort() ?? undefined) !==
          (c.getColDef().initialSort ?? undefined),
      ),
  );

  const didRowGroupChange = useAgGridEventState(
    agGrid,
    "columnRowGroupChanged",
    (api) =>
      api
        .getColumns()
        ?.some(
          (c) =>
            c.isRowGroupActive() !== (c.getColDef().initialRowGroup ?? false),
        ),
  );

  const didRowExpandChange = useAgGridEventState(
    agGrid,
    "modelUpdated",
    (api) =>
      api.getRenderedNodes().some((n) => !!(n.allChildrenCount && n.expanded)),
  );

  const didPivotChange = useAgGridEventState(
    agGrid,
    "columnPivotModeChanged",
    (api) => api.isPivotMode(),
  );

  if (
    !didPositionChange &&
    !didVisibilityOrFilterChange &&
    !didSortChange &&
    !didRowGroupChange &&
    !didRowExpandChange &&
    !didPivotChange
  ) {
    return null;
  }

  return (
    <Button
      variant="subtle"
      fontSize="12px"
      onClick={async () => {
        const api = tryGetAgGridApi(agGrid);
        if (!api) {
          return;
        }
        api.collapseAll();
        api.resetColumnState();
        api.setFilterModel(null);
        api.setPivotResultColumns(null);
        api.setGridOption("pivotMode", false);
        await sleep(300); // let animations finish
        api.autoSizeAllColumns();
      }}
    >
      {t("common:table.reset")}
    </Button>
  );
};

export const TableShareStateButton: React.FC = () => {
  const { agGrid } = useMaybernTableUIContext();
  const { t } = useTranslation(["common"]);
  const exportGridState = useExportGridStateUrl(agGrid);
  return (
    <Tooltip label={t("common:share")}>
      <IconButton
        aria-label={t("common:share")}
        variant="ghost"
        size="tablebox-icon"
        icon={<ShareIcon />}
        onClick={exportGridState}
      />
    </Tooltip>
  );
};

export const TableExportButton: React.FC<{
  getExportParams?: (agGrid: AgGridReact) => ExcelExportParams | undefined;
}> = ({ getExportParams }) => {
  const { t } = useTranslation(["common"]);

  const { title } = useTableBoxContext();
  const { agGrid, topLevelRowCount } = useMaybernTableUIContext();
  if (topLevelRowCount === 0) {
    return null;
  }

  return (
    <Tooltip label={t("common:exportToExcel")}>
      <IconButton
        variant="ghost"
        size="tablebox-icon"
        aria-label={t("common:exportToExcel")}
        icon={<FileDownloadIcon />}
        onClick={() => {
          /* eslint-disable @typescript-eslint/no-unnecessary-condition */
          if (agGrid) {
            agGrid.api.exportDataAsExcel(
              getExportParams?.(agGrid) ?? maybernTableExcelExportParams(title),
            );
          }
        }}
      />
    </Tooltip>
  );
};

export const TableClearFormButton = ({ onClear }: { onClear?: () => void }) => {
  const { t } = useTranslation(["common"]);

  const { agGrid } = useMaybernTableUIContext();

  const form = useFormContext();

  const clearForm = () => {
    const rows: GenericTableRow[] = agGrid?.props.rowData ?? [];

    const formFields = new Set(
      rows
        .map((row) =>
          toPairs(row.cells)
            .filter((cell) => !!cell[1].form_field)
            .map((cell) => cell[1].form_field!.name),
        )
        .flat(),
    );

    const clearValues = (
      obj: Record<string, string | object | null>,
      path = "",
    ) =>
      Object.keys(obj).reduce<Record<string, string | object | null>>(
        (acc, key) => {
          const fullPath = path ? `${path}.${key}` : key;
          if (typeof obj[key] === "object" && obj[key] !== null) {
            acc[key] = clearValues(
              obj[key] as Record<string, string | object | null>,
              fullPath,
            );
          } else {
            if (formFields.has(fullPath)) {
              acc[key] = "";
            }
          }
          return acc;
        },
        {},
      );
    const cleared = clearValues(cloneDeep(form.getValues()));
    form.reset(cleared);
    // clear form values of table
    onClear?.();

    // clear form values in ag-grid
    agGrid?.api.forEachNode((node: IRowNode<GenericTableRow>) => {
      node.setData({
        ...node.data!,
        cells: mapValues(node.data!.cells, (cell) => {
          if (!cell.form_field) {
            return cell;
          }
          return {
            ...cell,
            value: clearValueByType(cell),
          };
        }),
      });
    });
  };

  return (
    <Tooltip label={t("common:clearHelper")}>
      <Button colorScheme="secondary" onClick={() => clearForm()} size="xs">
        {t("common:table.clear")}
      </Button>
    </Tooltip>
  );
};

const clearValueByType = (cell: GenericTableRow["cells"][string]) => {
  if (!cell.form_field) {
    return cell.value;
  }

  switch (cell.value?.type) {
    case "money": {
      const newValue: MoneyTableCellValue = {
        ...cell.value,
        amount: "",
      };
      return newValue;
    }
    case "boolean": {
      const newValue: BooleanTableCellValue = {
        ...cell.value,
        value: false,
      };
      return newValue;
    }
    case "date": {
      const newValue: DateTableCellValue = {
        ...cell.value,
        date: "",
      };
      return newValue;
    }
    case "datetime": {
      const newValue: DatetimeTableCellValue = {
        ...cell.value,
        datetime: "",
      };
      return newValue;
    }
    case "text": {
      const newValue: TextTableCellValue = {
        ...cell.value,
        text: null,
      };
      return newValue;
    }
    case "decimal": {
      const newValue: DecimalTableCellValue = {
        ...cell.value,
        value: "",
      };
      return newValue;
    }
    case "integer": {
      const newValue: IntegerTableCellValue = {
        ...cell.value,
        value: 0,
      };
      return newValue;
    }
    case "formula": {
      const newValue: FormulaTableCellValue = {
        ...cell.value,
        text: "",
      };
      return newValue;
    }
    case "percent": {
      const newValue: PercentTableCellValue = {
        ...cell.value,
        value: "",
      };
      return newValue;
    }
    default: {
      return cell.value;
    }
  }
};
